<template>
  <main class="w-full max-w-none">
    <div class="mx-auto w-full max-w-full px-0">
      <div class="mb-8">
        <h1 class="text-2xl md:text-3xl font-bold text-gray-900 mb-2">
          批量转账(支付)制单
        </h1>
        <AdvancedFeatureNotice api-url="/api/v2/payment/batchTransfer" />
        <p class="text-gray-600">提交批量支付指令，生成批次支付单。</p>
      </div>

      <div class="grid grid-cols-1 lg:grid-cols-5 gap-3 w-full">
        <!-- 左侧内容 -->
        <div class="w-full lg:col-span-3">
          <Card class="mb-8">
            <CardHeader>
              <CardTitle>接口说明</CardTitle>
              <CardDescription>
                目前仅支持招商银行的批量支付制单（一对多付款，每一批次只能有一个付款账号，可以有多个收款账号）。
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div
                class="flex items-center gap-4 mb-6 p-4 bg-gray-50 rounded-lg"
              >
                <span
                  class="px-3 py-1 bg-green-100 text-green-800 text-sm font-medium rounded-full"
                  >POST</span
                >
                <code class="text-sm font-mono"
                  >/api/v2/payment/batchTransfer</code
                >
              </div>

              <div class="space-y-6">
                <!-- 国密加密传输说明已移至弹窗展示 -->
                <div>
                  <h3 class="text-lg font-medium text-gray-900 mb-3">
                    请求头参数
                  </h3>
                  <div class="overflow-x-auto">
                    <table class="min-w-max md:min-w-full divide-y divide-gray-200">
                      <thead class="bg-gray-50">
                        <tr>
                          <th
                            class="px-3 md:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                          >
                            参数名
                          </th>
                          <th
                            class="px-3 md:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                          >
                            类型
                          </th>
                          <th
                            class="px-3 md:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                          >
                            必需
                          </th>
                          <th
                            class="px-3 md:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                          >
                            描述
                          </th>
                          <th
                            class="px-3 md:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                          >
                            示例值
                          </th>
                        </tr>
                      </thead>
                      <tbody class="bg-white divide-y divide-gray-200">
                        <tr>
                          <td
                            class="px-3 md:px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900"
                          >
                            x-app-id
                          </td>
                          <td
                            class="px-3 md:px-6 py-4 whitespace-nowrap text-sm text-gray-500"
                          >
                            String
                          </td>
                          <td
                            class="px-3 md:px-6 py-4 whitespace-nowrap text-sm text-gray-500"
                          >
                            是
                          </td>
                          <td class="px-3 md:px-6 py-4 text-sm text-gray-500">
                            APP ID
                          </td>
                          <td class="px-3 md:px-6 py-4 text-sm text-gray-500">
                            4d9414e89bc24b0d89b678d9f20bc56a
                          </td>
                        </tr>
                        <tr>
                          <td
                            class="px-3 md:px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900"
                          >
                            x-app-secret
                          </td>
                          <td
                            class="px-3 md:px-6 py-4 whitespace-nowrap text-sm text-gray-500"
                          >
                            String
                          </td>
                          <td
                            class="px-3 md:px-6 py-4 whitespace-nowrap text-sm text-gray-500"
                          >
                            是
                          </td>
                          <td class="px-3 md:px-6 py-4 text-sm text-gray-500">
                            API Key
                          </td>
                          <td class="px-3 md:px-6 py-4 text-sm text-gray-500">
                            8201e508ef948b8fcdca855729e7ee34
                          </td>
                        </tr>
                        <tr>
                          <td
                            class="px-3 md:px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900"
                          >
                            x-customer-code
                          </td>
                          <td
                            class="px-3 md:px-6 py-4 whitespace-nowrap text-sm text-gray-500"
                          >
                            String
                          </td>
                          <td
                            class="px-3 md:px-6 py-4 whitespace-nowrap text-sm text-gray-500"
                          >
                            是
                          </td>
                          <td class="px-3 md:px-6 py-4 text-sm text-gray-500">
                            客户编号
                          </td>
                          <td class="px-3 md:px-6 py-4 text-sm text-gray-500">
                            CUSTFA48DD8EFF437956
                          </td>
                        </tr>
                        <tr>
                          <td
                            class="px-3 md:px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900"
                          >
                            Content-Type
                          </td>
                          <td
                            class="px-3 md:px-6 py-4 whitespace-nowrap text-sm text-gray-500"
                          >
                            String
                          </td>
                          <td
                            class="px-3 md:px-6 py-4 whitespace-nowrap text-sm text-gray-500"
                          >
                            是
                          </td>
                          <td class="px-3 md:px-6 py-4 text-sm text-gray-500">
                            发送内容类型
                          </td>
                          <td class="px-3 md:px-6 py-4 text-sm text-gray-500">
                            application/json
                          </td>
                        </tr>
                        <tr>
                          <td
                            class="px-3 md:px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900"
                          >
                            xencio-key
                          </td>
                          <td
                            class="px-3 md:px-6 py-4 whitespace-nowrap text-sm text-gray-500"
                          >
                            String
                          </td>
                          <td
                            class="px-3 md:px-6 py-4 whitespace-nowrap text-sm text-gray-500"
                          >
                            否
                          </td>
                          <td class="px-3 md:px-6 py-4 text-sm text-gray-500">
                            加密密钥，当启用国密加密的时候 该请求头必传
                          </td>
                          <td class="px-3 md:px-6 py-4 text-sm text-gray-500">
                            BM/UBCOn2yrZINuOdvaS01KjftyyRagN/LU2zVntwD8svrIObu/2Sqjl041C3RyqsUlE02ERmRBNgoPcoyhM7Tm1Quv55QcQ9hDNX0GO3997OANU7HQGHYlJ2aDEf0pTGNC0Lu5CzAwSM76dTorLyGI=
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>

                <div>
                  <h3 class="text-lg font-medium text-gray-900 mb-3">
                    请求参数
                  </h3>
                  <div class="overflow-x-auto">
                    <table class="min-w-max md:min-w-full divide-y divide-gray-200">
                      <thead class="bg-gray-50">
                        <tr>
                          <th
                            class="px-3 md:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                          >
                            参数名
                          </th>
                          <th
                            class="px-3 md:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                          >
                            类型
                          </th>
                          <th
                            class="px-3 md:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                          >
                            必需
                          </th>
                          <th
                            class="px-3 md:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                          >
                            描述
                          </th>
                          <th
                            class="px-3 md:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                          >
                            示例值
                          </th>
                        </tr>
                      </thead>
                      <tbody class="bg-white divide-y divide-gray-200">
                        <tr>
                          <td
                            class="px-3 md:px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900"
                          >
                            totalTrxNumber
                          </td>
                          <td
                            class="px-3 md:px-6 py-4 whitespace-nowrap text-sm text-gray-500"
                          >
                            String
                          </td>
                          <td
                            class="px-3 md:px-6 py-4 whitespace-nowrap text-sm text-gray-500"
                          >
                            是
                          </td>
                          <td class="px-3 md:px-6 py-4 text-sm text-gray-500">
                            批量转账总流水号，16位长度，全局唯一，不可重复
                          </td>
                          <td class="px-3 md:px-6 py-4 text-sm text-gray-500">
                            0826155929265328
                          </td>
                        </tr>
                        <tr>
                          <td
                            class="px-3 md:px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900"
                          >
                            totalRecord
                          </td>
                          <td
                            class="px-3 md:px-6 py-4 whitespace-nowrap text-sm text-gray-500"
                          >
                            String
                          </td>
                          <td
                            class="px-3 md:px-6 py-4 whitespace-nowrap text-sm text-gray-500"
                          >
                            是
                          </td>
                          <td class="px-3 md:px-6 py-4 text-sm text-gray-500">
                            总笔数，最大支持500笔
                          </td>
                          <td class="px-3 md:px-6 py-4 text-sm text-gray-500">2</td>
                        </tr>
                        <tr>
                          <td
                            class="px-3 md:px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900"
                          >
                            source
                          </td>
                          <td
                            class="px-3 md:px-6 py-4 whitespace-nowrap text-sm text-gray-500"
                          >
                            String
                          </td>
                          <td
                            class="px-3 md:px-6 py-4 whitespace-nowrap text-sm text-gray-500"
                          >
                            是
                          </td>
                          <td class="px-3 md:px-6 py-4 text-sm text-gray-500">
                            来源，值传 key 如 SAP/FK/NC/OA/JD
                          </td>
                          <td class="px-3 md:px-6 py-4 text-sm text-gray-500">OA</td>
                        </tr>
                        <tr>
                          <td
                            class="px-3 md:px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900"
                          >
                            paymentType
                          </td>
                          <td
                            class="px-3 md:px-6 py-4 whitespace-nowrap text-sm text-gray-500"
                          >
                            String
                          </td>
                          <td
                            class="px-3 md:px-6 py-4 whitespace-nowrap text-sm text-gray-500"
                          >
                            是
                          </td>
                          <td class="px-3 md:px-6 py-4 text-sm text-gray-500">
                            转账类型 realtime-实时付款；appoint-预约付款，默认
                            realtime
                          </td>
                          <td class="px-3 md:px-6 py-4 text-sm text-gray-500">
                            realtime
                          </td>
                        </tr>
                        <tr>
                          <td
                            class="px-3 md:px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900"
                          >
                            paymentDateTime
                          </td>
                          <td
                            class="px-3 md:px-6 py-4 whitespace-nowrap text-sm text-gray-500"
                          >
                            String
                          </td>
                          <td
                            class="px-3 md:px-6 py-4 whitespace-nowrap text-sm text-gray-500"
                          >
                            特定条件必填
                          </td>
                          <td class="px-3 md:px-6 py-4 text-sm text-gray-500">
                            预约时间，当 paymentType=appoint
                            时不可为空（yyyy-MM-dd HH:mm:ss）
                          </td>
                          <td class="px-3 md:px-6 py-4 text-sm text-gray-500">
                            2024-01-01 12:00:00
                          </td>
                        </tr>
                        <tr>
                          <td
                            class="px-3 md:px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900"
                          >
                            list
                          </td>
                          <td
                            class="px-3 md:px-6 py-4 whitespace-nowrap text-sm text-gray-500"
                          >
                            String
                          </td>
                          <td
                            class="px-3 md:px-6 py-4 whitespace-nowrap text-sm text-gray-500"
                          >
                            是
                          </td>
                          <td class="px-3 md:px-6 py-4 text-sm text-gray-500">
                            批量转账明细，JSON 数组字符串；包含明细字段见示例
                          </td>
                          <td class="px-3 md:px-6 py-4 text-sm text-gray-500">
                            [{"trxNumber":"202310300001","fromAccountNo":"755915704010801","toAccountNo":"6214831150131511","toAccountType":"0","toAccountName":"吴极客","toBank":"招商银行深圳分行","toBankNo":"12345678","payAmount":"1.00","note":"测试附言","uses":"测试用途","instructionId":"F000000001"}]
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>

                <div>
                  <h3 class="text-lg font-medium text-gray-900 mb-3">
                    响应参数
                  </h3>
                  <div class="overflow-x-auto">
                    <table class="min-w-max md:min-w-full divide-y divide-gray-200">
                      <thead class="bg-gray-50">
                        <tr>
                          <th
                            class="px-3 md:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                          >
                            参数名
                          </th>
                          <th
                            class="px-3 md:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                          >
                            类型
                          </th>
                          <th
                            class="px-3 md:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                          >
                            描述
                          </th>
                        </tr>
                      </thead>
                      <tbody class="bg-white divide-y divide-gray-200">
                        <tr>
                          <td
                            class="px-3 md:px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900"
                          >
                            code
                          </td>
                          <td
                            class="px-3 md:px-6 py-4 whitespace-nowrap text-sm text-gray-500"
                          >
                            String
                          </td>
                          <td
                            class="px-3 md:px-6 py-4 whitespace-nowrap text-sm text-gray-500"
                          >
                            是
                          </td>
                          <td class="px-3 md:px-6 py-4 text-sm text-gray-500">
                            返回码
                          </td>
                          <td class="px-3 md:px-6 py-4 text-sm text-gray-500">200</td>
                        </tr>
                        <tr>
                          <td
                            class="px-3 md:px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900"
                          >
                            data
                          </td>
                          <td
                            class="px-3 md:px-6 py-4 whitespace-nowrap text-sm text-gray-500"
                          >
                            Object
                          </td>
                          <td
                            class="px-3 md:px-6 py-4 whitespace-nowrap text-sm text-gray-500"
                          >
                            是
                          </td>
                          <td class="px-3 md:px-6 py-4 text-sm text-gray-500">
                            返回数据
                          </td>
                          <td class="px-3 md:px-6 py-4 text-sm text-gray-500">{...}</td>
                        </tr>
                        <tr>
                          <td
                            class="px-3 md:px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900"
                          >
                            data.code
                          </td>
                          <td
                            class="px-3 md:px-6 py-4 whitespace-nowrap text-sm text-gray-500"
                          >
                            String
                          </td>
                          <td
                            class="px-3 md:px-6 py-4 whitespace-nowrap text-sm text-gray-500"
                          >
                            是
                          </td>
                          <td class="px-3 md:px-6 py-4 text-sm text-gray-500">
                            状态码解释（见附录）
                          </td>
                          <td class="px-3 md:px-6 py-4 text-sm text-gray-500">C0000</td>
                        </tr>
                        <tr>
                          <td
                            class="px-3 md:px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900"
                          >
                            data.message
                          </td>
                          <td
                            class="px-3 md:px-6 py-4 whitespace-nowrap text-sm text-gray-500"
                          >
                            String
                          </td>
                          <td
                            class="px-3 md:px-6 py-4 whitespace-nowrap text-sm text-gray-500"
                          >
                            是
                          </td>
                          <td class="px-3 md:px-6 py-4 text-sm text-gray-500">
                            描述信息
                          </td>
                          <td class="px-3 md:px-6 py-4 text-sm text-gray-500">
                            提交成功
                          </td>
                        </tr>
                        <tr>
                          <td
                            class="px-3 md:px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900"
                          >
                            status
                          </td>
                          <td
                            class="px-3 md:px-6 py-4 whitespace-nowrap text-sm text-gray-500"
                          >
                            String
                          </td>
                          <td
                            class="px-3 md:px-6 py-4 whitespace-nowrap text-sm text-gray-500"
                          >
                            是
                          </td>
                          <td class="px-3 md:px-6 py-4 text-sm text-gray-500">
                            HTTP 状态码
                          </td>
                          <td class="px-3 md:px-6 py-4 text-sm text-gray-500">200</td>
                        </tr>
                        <tr>
                          <td
                            class="px-3 md:px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900"
                          >
                            successResponse
                          </td>
                          <td
                            class="px-3 md:px-6 py-4 whitespace-nowrap text-sm text-gray-500"
                          >
                            Boolean
                          </td>
                          <td
                            class="px-3 md:px-6 py-4 whitespace-nowrap text-sm text-gray-500"
                          >
                            是
                          </td>
                          <td class="px-3 md:px-6 py-4 text-sm text-gray-500">
                            是否成功
                          </td>
                          <td class="px-3 md:px-6 py-4 text-sm text-gray-500">true</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
          <div>
            <h3 class="text-lg font-medium text-gray-900 mb-3">附录</h3>
            <div class="overflow-x-auto">
              <table class="min-w-max md:min-w-full divide-y divide-gray-200">
                <thead class="bg-gray-50">
                  <tr>
                    <th
                      class="px-2 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                    >
                      序号
                    </th>
                    <th
                      class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                    >
                      返回码
                    </th>
                    <th
                      class="px-3 md:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                    >
                      描述
                    </th>
                  </tr>
                </thead>
                <tbody class="bg-white divide-y divide-gray-200">
                  <tr>
                    <td
                      class="px-2 py-4 whitespace-nowrap text-sm font-medium text-gray-900"
                    >
                      1
                    </td>
                    <td
                      class="px-4 py-4 whitespace-nowrap text-sm font-medium text-gray-900"
                    >
                      C0000
                    </td>
                    <td class="px-3 md:px-6 py-4 text-sm text-gray-500">提交成功</td>
                  </tr>
                  <tr>
                    <td
                      class="px-2 py-4 whitespace-nowrap text-sm font-medium text-gray-900"
                    >
                      2
                    </td>
                    <td
                      class="px-4 py-4 whitespace-nowrap text-sm font-medium text-gray-900"
                    >
                      C0001
                    </td>
                    <td class="px-3 md:px-6 py-4 text-sm text-gray-500">
                      付款账号不能为空
                    </td>
                  </tr>
                  <tr>
                    <td
                      class="px-2 py-4 whitespace-nowrap text-sm font-medium text-gray-900"
                    >
                      3
                    </td>
                    <td
                      class="px-4 py-4 whitespace-nowrap text-sm font-medium text-gray-900"
                    >
                      C0002
                    </td>
                    <td class="px-3 md:px-6 py-4 text-sm text-gray-500">
                      付款账户名不能为空
                    </td>
                  </tr>
                  <tr>
                    <td
                      class="px-2 py-4 whitespace-nowrap text-sm font-medium text-gray-900"
                    >
                      4
                    </td>
                    <td
                      class="px-4 py-4 whitespace-nowrap text-sm font-medium text-gray-900"
                    >
                      C0003
                    </td>
                    <td class="px-3 md:px-6 py-4 text-sm text-gray-500">
                      付款银行不能为空
                    </td>
                  </tr>
                  <tr>
                    <td
                      class="px-2 py-4 whitespace-nowrap text-sm font-medium text-gray-900"
                    >
                      5
                    </td>
                    <td
                      class="px-4 py-4 whitespace-nowrap text-sm font-medium text-gray-900"
                    >
                      C0004
                    </td>
                    <td class="px-3 md:px-6 py-4 text-sm text-gray-500">
                      收款账号不能为空
                    </td>
                  </tr>
                  <tr>
                    <td
                      class="px-2 py-4 whitespace-nowrap text-sm font-medium text-gray-900"
                    >
                      6
                    </td>
                    <td
                      class="px-4 py-4 whitespace-nowrap text-sm font-medium text-gray-900"
                    >
                      C0005
                    </td>
                    <td class="px-3 md:px-6 py-4 text-sm text-gray-500">
                      收款账户名不能为空
                    </td>
                  </tr>
                  <tr>
                    <td
                      class="px-2 py-4 whitespace-nowrap text-sm font-medium text-gray-900"
                    >
                      7
                    </td>
                    <td
                      class="px-4 py-4 whitespace-nowrap text-sm font-medium text-gray-900"
                    >
                      C0006
                    </td>
                    <td class="px-3 md:px-6 py-4 text-sm text-gray-500">
                      收款银行不能为空
                    </td>
                  </tr>
                  <tr>
                    <td
                      class="px-2 py-4 whitespace-nowrap text-sm font-medium text-gray-900"
                    >
                      8
                    </td>
                    <td
                      class="px-4 py-4 whitespace-nowrap text-sm font-medium text-gray-900"
                    >
                      C0007
                    </td>
                    <td class="px-3 md:px-6 py-4 text-sm text-gray-500">
                      收款账户类型不能为空
                    </td>
                  </tr>
                  <tr>
                    <td
                      class="px-2 py-4 whitespace-nowrap text-sm font-medium text-gray-900"
                    >
                      9
                    </td>
                    <td
                      class="px-4 py-4 whitespace-nowrap text-sm font-medium text-gray-900"
                    >
                      C0008
                    </td>
                    <td class="px-3 md:px-6 py-4 text-sm text-gray-500">
                      收款账户类型值不正确
                    </td>
                  </tr>
                  <tr>
                    <td
                      class="px-2 py-4 whitespace-nowrap text-sm font-medium text-gray-900"
                    >
                      10
                    </td>
                    <td
                      class="px-4 py-4 whitespace-nowrap text-sm font-medium text-gray-900"
                    >
                      C0009
                    </td>
                    <td class="px-3 md:px-6 py-4 text-sm text-gray-500">
                      转账金额不能为空
                    </td>
                  </tr>
                  <tr>
                    <td
                      class="px-2 py-4 whitespace-nowrap text-sm font-medium text-gray-900"
                    >
                      11
                    </td>
                    <td
                      class="px-4 py-4 whitespace-nowrap text-sm font-medium text-gray-900"
                    >
                      C0010
                    </td>
                    <td class="px-3 md:px-6 py-4 text-sm text-gray-500">
                      转账金额格式错误
                    </td>
                  </tr>
                  <tr>
                    <td
                      class="px-2 py-4 whitespace-nowrap text-sm font-medium text-gray-900"
                    >
                      12
                    </td>
                    <td
                      class="px-4 py-4 whitespace-nowrap text-sm font-medium text-gray-900"
                    >
                      C0011
                    </td>
                    <td class="px-3 md:px-6 py-4 text-sm text-gray-500">
                      用途不能为空
                    </td>
                  </tr>
                  <tr>
                    <td
                      class="px-2 py-4 whitespace-nowrap text-sm font-medium text-gray-900"
                    >
                      13
                    </td>
                    <td
                      class="px-4 py-4 whitespace-nowrap text-sm font-medium text-gray-900"
                    >
                      C0012
                    </td>
                    <td class="px-3 md:px-6 py-4 text-sm text-gray-500">
                      银行流水号不能为空
                    </td>
                  </tr>
                  <tr>
                    <td
                      class="px-2 py-4 whitespace-nowrap text-sm font-medium text-gray-900"
                    >
                      14
                    </td>
                    <td
                      class="px-4 py-4 whitespace-nowrap text-sm font-medium text-gray-900"
                    >
                      C0013
                    </td>
                    <td class="px-3 md:px-6 py-4 text-sm text-gray-500">
                      收款银行行号不能为空
                    </td>
                  </tr>
                  <tr>
                    <td
                      class="px-2 py-4 whitespace-nowrap text-sm font-medium text-gray-900"
                    >
                      15
                    </td>
                    <td
                      class="px-4 py-4 whitespace-nowrap text-sm font-medium text-gray-900"
                    >
                      C0014
                    </td>
                    <td class="px-3 md:px-6 py-4 text-sm text-gray-500">
                      交易流水号或电子凭证号已存在
                    </td>
                  </tr>
                  <tr>
                    <td
                      class="px-2 py-4 whitespace-nowrap text-sm font-medium text-gray-900"
                    >
                      16
                    </td>
                    <td
                      class="px-4 py-4 whitespace-nowrap text-sm font-medium text-gray-900"
                    >
                      C0015
                    </td>
                    <td class="px-3 md:px-6 py-4 text-sm text-gray-500">
                      付款账号系统中不存在
                    </td>
                  </tr>
                  <tr>
                    <td
                      class="px-2 py-4 whitespace-nowrap text-sm font-medium text-gray-900"
                    >
                      17
                    </td>
                    <td
                      class="px-4 py-4 whitespace-nowrap text-sm font-medium text-gray-900"
                    >
                      C0016
                    </td>
                    <td class="px-3 md:px-6 py-4 text-sm text-gray-500">
                      付款账号未开通银企直连
                    </td>
                  </tr>
                  <tr>
                    <td
                      class="px-2 py-4 whitespace-nowrap text-sm font-medium text-gray-900"
                    >
                      18
                    </td>
                    <td
                      class="px-4 py-4 whitespace-nowrap text-sm font-medium text-gray-900"
                    >
                      C0017
                    </td>
                    <td class="px-3 md:px-6 py-4 text-sm text-gray-500">
                      没有提交权限
                    </td>
                  </tr>
                  <tr>
                    <td
                      class="px-2 py-4 whitespace-nowrap text-sm font-medium text-gray-900"
                    >
                      19
                    </td>
                    <td
                      class="px-4 py-4 whitespace-nowrap text-sm font-medium text-gray-900"
                    >
                      C0018
                    </td>
                    <td class="px-3 md:px-6 py-4 text-sm text-gray-500">提交失败</td>
                  </tr>
                  <tr>
                    <td
                      class="px-2 py-4 whitespace-nowrap text-sm font-medium text-gray-900"
                    >
                      20
                    </td>
                    <td
                      class="px-4 py-4 whitespace-nowrap text-sm font-medium text-gray-900"
                    >
                      C0019
                    </td>
                    <td class="px-3 md:px-6 py-4 text-sm text-gray-500">
                      转账附言过长
                    </td>
                  </tr>
                  <tr>
                    <td
                      class="px-2 py-4 whitespace-nowrap text-sm font-medium text-gray-900"
                    >
                      21
                    </td>
                    <td
                      class="px-4 py-4 whitespace-nowrap text-sm font-medium text-gray-900"
                    >
                      C0020
                    </td>
                    <td class="px-3 md:px-6 py-4 text-sm text-gray-500">
                      交易流水号或电子凭证号不能同时为空
                    </td>
                  </tr>
                  <tr>
                    <td
                      class="px-2 py-4 whitespace-nowrap text-sm font-medium text-gray-900"
                    >
                      22
                    </td>
                    <td
                      class="px-4 py-4 whitespace-nowrap text-sm font-medium text-gray-900"
                    >
                      C0021
                    </td>
                    <td class="px-3 md:px-6 py-4 text-sm text-gray-500">
                      收款账号格式错误
                    </td>
                  </tr>
                  <tr>
                    <td
                      class="px-2 py-4 whitespace-nowrap text-sm font-medium text-gray-900"
                    >
                      23
                    </td>
                    <td
                      class="px-4 py-4 whitespace-nowrap text-sm font-medium text-gray-900"
                    >
                      C0022
                    </td>
                    <td class="px-3 md:px-6 py-4 text-sm text-gray-500">
                      收款账号长度不能超过40个字符
                    </td>
                  </tr>
                  <tr>
                    <td
                      class="px-2 py-4 whitespace-nowrap text-sm font-medium text-gray-900"
                    >
                      24
                    </td>
                    <td
                      class="px-4 py-4 whitespace-nowrap text-sm font-medium text-gray-900"
                    >
                      C0023
                    </td>
                    <td class="px-3 md:px-6 py-4 text-sm text-gray-500">
                      转账金额不能小于或等于0
                    </td>
                  </tr>
                  <tr>
                    <td
                      class="px-2 py-4 whitespace-nowrap text-sm font-medium text-gray-900"
                    >
                      25
                    </td>
                    <td
                      class="px-4 py-4 whitespace-nowrap text-sm font-medium text-gray-900"
                    >
                      C0024
                    </td>
                    <td class="px-3 md:px-6 py-4 text-sm text-gray-500">
                      付款单来源未配置
                    </td>
                  </tr>
                  <tr>
                    <td
                      class="px-2 py-4 whitespace-nowrap text-sm font-medium text-gray-900"
                    >
                      26
                    </td>
                    <td
                      class="px-4 py-4 whitespace-nowrap text-sm font-medium text-gray-900"
                    >
                      C0025
                    </td>
                    <td class="px-3 md:px-6 py-4 text-sm text-gray-500">
                      付款单来源不能为空
                    </td>
                  </tr>
                  <tr>
                    <td
                      class="px-2 py-4 whitespace-nowrap text-sm font-medium text-gray-900"
                    >
                      27
                    </td>
                    <td
                      class="px-4 py-4 whitespace-nowrap text-sm font-medium text-gray-900"
                    >
                      C0038
                    </td>
                    <td class="px-3 md:px-6 py-4 text-sm text-gray-500">
                      未找到对应的审批模版
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>

        <!-- 右侧"试一试"区域 -->
        <div class="w-full lg:col-span-2">
          <Card>
            <CardHeader>
              <CardTitle>试一试</CardTitle>
              <CardDescription>使用不同语言测试API</CardDescription>
            </CardHeader>
            <CardContent>
              <div class="border-b border-gray-200">
                <nav class="-mb-px flex space-x-8 overflow-x-auto">
                  <button
                    v-for="language in languages"
                    :key="language.id"
                    @click="activeLanguage = language.id"
                    class="whitespace-nowrap py-2 px-1 border-b-2 font-medium text-sm"
                    :class="[
                      activeLanguage === language.id
                        ? 'border-[var(--primary)] text-[var(--primary)]'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300',
                    ]"
                  >
                    {{ language.name }}
                  </button>
                </nav>
              </div>

              <div class="mt-6">
                <!-- 国密示例（与单笔页面一致展示逻辑） -->
                <label class="inline-flex items-center gap-2 mb-3 select-none">
                  <input
                    type="checkbox"
                    v-model="enableGm"
                    class="rounded border-gray-300 text-[var(--primary)] focus:ring-[var(--primary)]"
                  />
                  <span class="text-sm text-gray-700"
                    >启用国密加密（SM2/SM3/SM4）</span
                  >
                  <button
                    type="button"
                    class="ml-1 inline-flex items-center justify-center w-5 h-5 rounded-full bg-blue-100 text-blue-700 text-xs font-bold hover:bg-blue-200"
                    @click="showGmHelp = true"
                    aria-label="查看国密加密传输说明"
                  >
                    ?
                  </button>
                </label>

                <!-- 国密说明弹窗 -->
                <div v-if="showGmHelp" class="fixed inset-0 z-50">
                  <div
                    class="absolute inset-0 bg-black/40"
                    @click="showGmHelp = false"
                  ></div>
                  <div
                    class="relative mx-auto my-8 w-full max-w-[90vw] md:max-w-2xl"
                  >
                    <div
                      class="bg-white rounded-lg shadow-lg max-h-[85vh] flex flex-col"
                    >
                      <div
                        class="px-4 py-3 border-b border-gray-200 flex items-center justify-end"
                      >
                        <h3 class="sr-only">国密加密传输说明</h3>
                        <button
                          type="button"
                          class="text-gray-500 hover:text-gray-700"
                          @click="showGmHelp = false"
                        >
                          ✕
                        </button>
                      </div>
                      <div class="px-4 py-4 overflow-y-auto">
                        <GmHelpContent />
                        <!-- 保持与页面卡片完全一致的结构与样式（批量字段版本） -->
                        <!-- <div class="rounded-lg border border-blue-200 bg-blue-50 p-4">
                          <div class="flex items-center justify-between mb-2">
                            <h3 class="text-lg font-semibold text-blue-900">国密加密传输说明</h3>
                            <span class="inline-flex items-center px-2 py-0.5 text-xs font-medium rounded-full bg-blue-100 text-blue-700">SM2 / SM3 / SM4</span>
                          </div>
                          <div class="space-y-3 text-sm text-blue-900 leading-6">
                            <p>
                              本接口支持国密加密（SM2/SM3/SM4）。<br />启用方式：企业准备一套SM2密钥（公钥交付给见知，私钥自行保存），同时保存见知提供的见知公钥。
                            </p>
                            <div>
                              <p class="font-medium">启用国密加密的请求步骤：</p>
                              <ol class="list-decimal list-inside space-y-1">
                                <li>随机生成一个16位字符串作为SM4密钥（仅数字和大小写字母，不含特殊字符）。</li>
                                <li>将原始报文使用步骤1的SM4密钥进行加密，得到密文，赋值给 <code class="bg-white/60 px-1 py-0.5 rounded">reqData</code> 参数。原始报文把请求参数转换成json字符串：
                                  <code class="bg-white/60 px-1 py-0.5 rounded break-all whitespace-normal inline-block max-w-full">{"totalTrxNumber":"","totalRecord":"","source":"","paymentType":"realtime","paymentDateTime":"","list":[]}</code>（无值传空字符串）。</li>
                                <li>使用见知公钥（SM2）加密步骤1的SM4密钥，赋值到请求 Header 的 <code class="bg-white/60 px-1 py-0.5 rounded">xencio-key</code>。</li>
                                <li>使用SM3算法对原始报文进行摘要，得到一个字符串，对这个字符串用企业私钥进行签名，得到签名字符串，赋值给 <code class="bg-white/60 px-1 py-0.5 rounded">sign</code> 参数。</li>
                                <li>最终请求体：<code class="bg-white/60 px-1 py-0.5 rounded">{ "reqData": "...", "sign": "..." }</code>，并携带上述 Header。</li>
                              </ol>
                            </div>
                            <div>
                              <p class="font-medium">启用国密加密的响应解析：</p>
                              <ol class="list-decimal list-inside space-y-1">
                                <li>读取响应 Header 的 <code class="bg-white/60 px-1 py-0.5 rounded">xencio-key</code>，用企业SM2私钥解密，得到SM4密钥。</li>
                                <li>用步骤1得到的SM4密钥解密响应体中的 <code class="bg-white/60 px-1 py-0.5 rounded">respData</code>，得到原始响应报文的json字符串。</li>
                                <li>对步骤2得到的原始响应报文执行SM3摘要。</li>
                                <li>将步骤3得到的摘要，与响应体中获取到的签名（sign参数）字符串，使用见知公钥进行验签，如一致则通过。</li>
                              </ol>
                            </div>
                            <p class="text-xs text-blue-700">提示：原始响应报文即为当前接口的响应参数。</p>
                          </div>
                        </div> -->
                      </div>
                      <div
                        class="px-4 py-3 border-t border-gray-200 flex justify-end"
                      >
                        <button
                          type="button"
                          class="px-3 py-1.5 rounded bg-[var(--primary)] text-white hover:opacity-90"
                          @click="showGmHelp = false"
                        >
                          关闭
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
                <pre
                  v-if="!enableGm"
                  class="bg-gray-900 text-gray-100 p-4 rounded-md text-sm overflow-x-auto mt-3"
                ><code>{{ getRequestText() }}</code></pre>
                <pre
                  v-if="enableGm"
                  class="bg-gray-900 text-gray-100 p-4 rounded-md text-sm overflow-x-auto mt-3"
                ><code>{{ getGmSampleText() }}</code></pre>
                <!-- 国密加密开关与密钥输入 -->
                <div
                  v-if="enableGm"
                  class="pt-2 border-t border-gray-200"
                  style="margin-bottom: 0.5rem"
                >
                  <div
                    class="mt-4 rounded-lg border border-gray-200 bg-gray-50"
                  >
                    <div class="flex items-center justify-between px-3 py-2">
                      <div class="text-sm font-medium text-gray-800">
                        国密加密传输（SM2/SM3/SM4）
                      </div>
                      <span
                        v-if="enableGm"
                        class="text-xs px-2 py-0.5 rounded bg-green-100 text-green-700"
                        >已启用</span
                      >
                    </div>
                    <div class="flex items-center justify-between px-3 py-2">
                      <a
                        :href="getBaseDomain() + '/#/app-params'"
                        target="_blank"
                        class="card-link"
                        style="
                          color: var(--primary);
                          font-weight: 500;
                          font-style: italic;
                        "
                      >
                        <KeyOutlined class="w-4 h-4" />
                        获取/配置密钥
                      </a>
                    </div>
                    <div class="px-3 pb-3">
                      <div class="grid grid-cols-1 gap-3">
                        <div>
                          <label
                            class="block text-sm font-medium text-gray-700 mb-1"
                            >见知公钥（SM2 公钥，Base64）</label
                          >
                          <textarea
                            v-model="xencioPublicKey"
                            rows="3"
                            class="w-full px-3 py-2 font-mono tracking-wider border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-[var(--primary)] focus:border-[var(--primary)] resize-y"
                            placeholder="粘贴见知提供的 SM2 公钥（Base64，标准编码）"
                          ></textarea>
                          <div class="mt-1 flex items-center justify-between">
                            <p class="text-xs text-gray-500">
                              用于加密 <code>reqData</code>、<code>sign</code>
                              以及 Header <code>xencio-key</code>。
                            </p>
                            <div class="flex items-center gap-2">
                              <span
                                v-if="
                                  xencioPublicKey && isBase64(xencioPublicKey)
                                "
                                class="text-xs px-1.5 py-0.5 rounded bg-green-100 text-green-700"
                                >Base64格式正确</span
                              >
                              <span
                                v-else-if="
                                  xencioPublicKey && !isBase64(xencioPublicKey)
                                "
                                class="text-xs px-1.5 py-0.5 rounded bg-red-100 text-red-700"
                                >Base64格式错误</span
                              >
                              <button
                                type="button"
                                class="text-xs text-gray-500 hover:text-gray-700"
                                @click="xencioPublicKey = ''"
                              >
                                清空
                              </button>
                            </div>
                          </div>
                        </div>
                        <div>
                          <label
                            class="block text-sm font-medium text-gray-700 mb-1"
                            >企业 SM2 私钥（Base64）</label
                          >
                          <textarea
                            v-model="sm2PrivateKey"
                            rows="3"
                            class="w-full px-3 py-2 font-mono tracking-wider border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-[var(--primary)] focus:border-[var(--primary)] resize-y"
                            placeholder="粘贴企业自有 SM2 私钥（Base64，标准编码）"
                          ></textarea>
                          <div class="mt-1 flex items-center justify-between">
                            <p class="text-xs text-gray-500">
                              用于解密响应 <code>respData</code>、<code
                                >sign</code
                              >
                              以及 Header <code>xencio-key</code>。
                            </p>
                            <div class="flex items-center gap-2">
                              <span
                                v-if="sm2PrivateKey && isBase64(sm2PrivateKey)"
                                class="text-xs px-1.5 py-0.5 rounded bg-green-100 text-green-700"
                                >Base64格式正确</span
                              >
                              <span
                                v-else-if="
                                  sm2PrivateKey && !isBase64(sm2PrivateKey)
                                "
                                class="text-xs px-1.5 py-0.5 rounded bg-red-100 text-red-700"
                                >Base64格式错误</span
                              >
                              <button
                                type="button"
                                class="text-xs text-gray-500 hover:text-gray-700"
                                @click="sm2PrivateKey = ''"
                              >
                                清空
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div class="space-y-4">
                  <div>
                    <label class="block text-sm font-medium text-gray-700 mb-1"
                      >x-app-id</label
                    >
                    <input
                      v-model="appId"
                      type="text"
                      class="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-[var(--primary)] focus:border-[var(--primary)]"
                    />
                  </div>
                  <div>
                    <label class="block text-sm font-medium text-gray-700 mb-1"
                      >x-app-secret</label
                    >
                    <input
                      v-model="authorization"
                      type="text"
                      class="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-[var(--primary)] focus:border-[var(--primary)]"
                    />
                  </div>
                  <div>
                    <label class="block text-sm font-medium text-gray-700 mb-1"
                      >x-customer-code</label
                    >
                    <input
                      v-model="customerCode"
                      type="text"
                      class="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>

                  <div>
                    <label class="block text-sm font-medium text-gray-700"
                      >totalTrxNumber</label
                    >
                    <input
                      v-model="totalTrxNumber"
                      type="text"
                      class="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                      placeholder="0826155929265328"
                    />
                  </div>
                  <div>
                    <label class="block text-sm font-medium text-gray-700"
                      >totalRecord</label
                    >
                    <input
                      v-model="totalRecord"
                      type="text"
                      class="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                      placeholder="2"
                    />
                  </div>
                  <div>
                    <label class="block text-sm font-medium text-gray-700"
                      >source</label
                    >
                    <input
                      v-model="source"
                      type="text"
                      class="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                      placeholder="OA"
                    />
                  </div>
                  <div>
                    <label class="block text-sm font-medium text-gray-700"
                      >paymentType</label
                    >
                    <input
                      v-model="paymentType"
                      type="text"
                      class="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                      placeholder="realtime"
                    />
                  </div>
                  <div>
                    <label class="block text-sm font-medium text-gray-700"
                      >paymentDateTime</label
                    >
                    <input
                      v-model="paymentDateTime"
                      type="text"
                      class="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-[var(--primary)] focus:border-[var(--primary)]"
                      placeholder="2024-01-01 12:00:00"
                    />
                  </div>
                  <div class="col-span-2">
                    <label class="block text-sm font-medium text-gray-700"
                      >list</label
                    >
                    <textarea
                      v-model="list"
                      rows="6"
                      class="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-[var(--primary)] focus:border-[var(--primary)]"
                      placeholder='[{"trxNumber":"202310300001","fromAccountNo":"755915704010801","fromAccountName":"企业网银新20161341","toAccountNo":"6214831150131511","toAccountType":"0","toAccountName":"吴极客","toBank":"招商银行深圳分行","toBankNo":"12345678","payAmount":"1.00","note":"这是一个测试附言","uses":"这是一个测试用途","instructionId":"F000000001"}]'
                    ></textarea>
                  </div>

                  <button
                    @click="submitPayment"
                    :disabled="loading"
                    class="w-full bg-[var(--primary)] hover:bg-[color:var(--primary)] text-white font-medium py-2 px-4 rounded-md transition duration-200 ease-in-out transform hover:scale-105 focus:outline-none focus:ring-2 focus:ring-[var(--primary)] focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {{ loading ? "提交中..." : "发起批量支付制单请求" }}
                  </button>
                </div>
              </div>

              <!-- 响应结果 -->
              <div v-if="response" class="mt-6">
                <h3 class="text-lg font-medium text-gray-900 mb-2">响应结果</h3>
                <pre
                  class="bg-gray-800 text-gray-100 p-4 rounded-md text-sm overflow-x-auto"
                ><code>{{ response }}</code></pre>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  </main>
</template>

<script setup lang="ts">
import { ref, onMounted, watch, nextTick } from "vue";
import Sidebar from "./Sidebar.vue";
import Card from "./ui/Card.vue";
import CardHeader from "./ui/CardHeader.vue";
import CardTitle from "./ui/CardTitle.vue";
import CardDescription from "./ui/CardDescription.vue";
import CardContent from "./ui/CardContent.vue";
import AdvancedFeatureNotice from "./ui/AdvancedFeatureNotice.vue";
import { useCodeHighlight } from "@/composables/useCodeHighlight";
import httpService from "@/lib/http";
import hljs from "highlight.js/lib/core";
import CryptoJS from "crypto-js";
import { useSystemStore } from "@/stores/system";
import { getBaseDomain } from "@/lib/config";
import GmHelpContent from "./ui/GmHelpContent.vue";
const serverUrl = import.meta.env.VITE_API_URL;
// 国密：动态引入 sm-crypto，避免打包兼容问题
// 使用代码高亮
const highlightCode = () => {
  nextTick(() => {
    // 等待DOM更新完成后再处理
    setTimeout(() => {
      // 找到所有代码块
      const allCodeBlocks = document.querySelectorAll("pre code");

      // 语言映射表
      const languageMap: { [key: string]: string } = {
        curl: "language-bash",
        javascript: "language-javascript",
        python: "language-python",
        java: "language-java",
        csharp: "language-csharp",
        php: "language-php",
        ruby: "language-ruby",
      };

      const langClass = languageMap[activeLanguage.value];

      // 处理所有代码块
      allCodeBlocks.forEach((block) => {
        // 清除之前的高亮
        block.className = "";
        block.removeAttribute("data-highlighted");

        // 重新添加基础类和语言类
        block.classList.add("hljs");
        if (langClass) {
          block.classList.add(langClass);
        }

        try {
          hljs.highlightElement(block as HTMLElement);
        } catch (error) {
          console.warn("代码高亮失败:", error);
        }
      });
    }, 100); // 减少延迟时间
  });
};

// 初始化代码高亮
highlightCode();

// 系统配置
const systemStore = useSystemStore();

// 默认值
const DEFAULT_APP_ID = "登录后自动获取";
const DEFAULT_APP_SECRET = "登录后自动获取";
const DEFAULT_CUSTOMER_CODE = "登录后自动获取";
const DEFAULT_XENCIO_PUBLIC_KEY = "登录后自动获取";

const appId = ref(DEFAULT_APP_ID);
const authorization = ref(DEFAULT_APP_SECRET);
const customerCode = ref(DEFAULT_CUSTOMER_CODE);
const xencioPublicKey = ref(DEFAULT_XENCIO_PUBLIC_KEY);
// 支付制单字段
const acceptHeader = ref("application/json");
const totalTrxNumber = ref("");
const totalRecord = ref("");
const source = ref("");
const paymentType = ref("realtime");
const paymentDateTime = ref("");
const list = ref(
  '[{"trxNumber":"202310300001","fromAccountNo":"755915704010801","fromAccountName":"企业网银新20161341","toAccountNo":"6214831150131511","toAccountType":"0","toAccountName":"吴极客","toBank":"招商银行深圳分行","toBankNo":"12345678","payAmount":"1.00","note":"这是一个测试附言","uses":"这是一个测试用途","instructionId":"F000000001"},{"trxNumber":"202310300002","fromAccountNo":"755915704010801","fromAccountName":"企业网银新20161341","toAccountNo":"6214831150131511","toAccountType":"0","toAccountName":"吴极客","toBank":"招商银行深圳分行","toBankNo":"12345678","payAmount":"1.00","note":"这是一个测试附言","uses":"这是一个测试用途","instructionId":"F000000002"}]'
);

const loading = ref(false);
const response = ref("");
const activeLanguage = ref("curl");

// 国密相关（与单笔页面保持一致）
const enableGm = ref(true);
const showGmHelp = ref(false);
const sm2PrivateKey = ref("");
let sm2Lib: any = null;
let sm3Lib: any = null;
let sm4Lib: any = null;
const ensureSmCrypto = async () => {
  if (sm2Lib && sm3Lib && sm4Lib) return;
  try {
    const mod: any = await import("sm-crypto");
    const m = mod && mod.default ? mod.default : mod;
    sm2Lib = m?.sm2;
    sm3Lib = m?.sm3;
    sm4Lib = m?.sm4;
    if (
      !sm2Lib ||
      typeof sm2Lib.doEncrypt !== "function" ||
      !sm3Lib ||
      !sm4Lib
    ) {
      throw new Error("sm-crypto 加载异常：sm2/sm3/sm4 不可用或导入方式不兼容");
    }
  } catch (e) {
    console.warn(
      "未能加载 sm-crypto 库，请确认依赖已安装并与当前打包方式兼容。"
    );
    throw e;
  }
};
const randomSm4Key = () => {
  const chars =
    "0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz";
  let key = "";
  for (let i = 0; i < 16; i++)
    key += chars.charAt(Math.floor(Math.random() * chars.length));
  return key;
};

// Base64校验（标准Base64）
const isBase64 = (s: unknown) => {
  const str = String(s ?? "");
  if (!str || str.length % 4 !== 0) return false;
  if (!/^[A-Za-z0-9+/=]+$/.test(str)) return false;
  try {
    // atob会在非法输入时报错
    atob(str);
    return true;
  } catch {
    return false;
  }
};

const base64ToBytes = (b64: string) => {
  const s = String(b64 || "").replace(/\s+/g, "");
  const pad = s.length % 4 ? "=".repeat(4 - (s.length % 4)) : "";
  const raw = atob(s + pad);
  return new Uint8Array(Array.from(raw).map((c) => c.charCodeAt(0)));
};
const bytesToHex = (arr: Uint8Array) =>
  Array.from(arr)
    .map((b) => b.toString(16).padStart(2, "0"))
    .join("");
const readDerLength = (buf: Uint8Array, pos: number) => {
  let len = buf[pos];
  pos += 1;
  if ((len & 0x80) !== 0) {
    const n = len & 0x7f;
    let v = 0;
    for (let i = 0; i < n; i++) v = (v << 8) | buf[pos + i];
    pos += n;
    len = v;
  }
  return { len, pos };
};

const spkiBase64ToUncompressedHex = (b64: string) => {
  const outer = base64ToBytes(b64);
  if (outer[0] !== 0x30) throw new Error("公钥DER解析失败: 非SEQUENCE");
  const { len, pos } = readDerLength(outer, 1);
  const content = outer.subarray(pos, pos + len);
  let p = 0;
  if (content[p] !== 0x30) throw new Error("公钥DER解析失败: 算法标识缺失");
  p += 1;
  const r1 = readDerLength(content, p);
  p = r1.pos + r1.len;
  if (content[p] !== 0x03) throw new Error("公钥DER解析失败: 未找到BIT STRING");
  p += 1;
  const r2 = readDerLength(content, p);
  const bitStart = r2.pos;
  const bitLen = r2.len;
  const unused = content[bitStart];
  if (unused !== 0x00) throw new Error("公钥DER解析失败: 非零unused bits");
  const point = content.subarray(bitStart + 1, bitStart + bitLen);
  if (point.length !== 65 || point[0] !== 0x04)
    throw new Error("公钥格式错误: 需非压缩点");
  return bytesToHex(point);
};
const pkcs8Base64ToPrivateKeyHex = (b64: string) => {
  const buf = base64ToBytes(b64);
  let p = 0;
  if (buf[p++] !== 0x30) throw new Error("私钥DER解析失败: 非SEQUENCE");
  let r = readDerLength(buf, p);
  const end = r.pos + r.len;
  p = r.pos;
  if (buf[p++] !== 0x02) throw new Error("私钥DER解析失败: 缺少版本INTEGER");
  r = readDerLength(buf, p);
  p = r.pos + r.len;
  if (buf[p++] !== 0x30) throw new Error("私钥DER解析失败: 缺少算法SEQUENCE");
  r = readDerLength(buf, p);
  p = r.pos + r.len;
  if (buf[p++] !== 0x04) throw new Error("私钥DER解析失败: 未找到OCTET STRING");
  r = readDerLength(buf, p);
  const ecStart = r.pos;
  const ecLen = r.len;
  const ec = buf.subarray(ecStart, ecStart + ecLen);
  let q = 0;
  if (ec[q++] !== 0x30)
    throw new Error("私钥DER解析失败: 非ECPrivateKey SEQUENCE");
  let r2 = readDerLength(ec, q);
  const ecEnd = r2.pos + r2.len;
  q = r2.pos;
  if (ec[q++] !== 0x02) throw new Error("私钥DER解析失败: 缺少EC版本INTEGER");
  r2 = readDerLength(ec, q);
  q = r2.pos + r2.len;
  if (ec[q++] !== 0x04)
    throw new Error("私钥DER解析失败: 未找到ECPrivateKey OCTET STRING");
  r2 = readDerLength(ec, q);
  const privStart = r2.pos;
  const privLen = r2.len;
  let priv = ec.subarray(privStart, privStart + privLen);
  if (priv.length > 32) priv = priv.slice(priv.length - 32);
  if (priv.length < 32)
    priv = new Uint8Array([
      ...new Array(32 - priv.length).fill(0),
      ...Array.from(priv),
    ]);
  return bytesToHex(priv as Uint8Array);
};
const normalizeSm2PublicHex = (s: string) => {
  if (isBase64(s)) return spkiBase64ToUncompressedHex(s);
  return s;
};
const normalizeSm2PrivateHex = (s: string) => {
  if (isBase64(s)) return pkcs8Base64ToPrivateKeyHex(s);
  return s;
};

// 获取应用信息
const getApplicationInfo = async () => {
  try {
    const response = await httpService.get<any>("/nxb/console/application/get");

    if (response) {
      return {
        appId: response.appId,
        appSecret: response.appSecret,
        xencioPublicKey: response.xencioPublicKey,
      };
    }
    return null;
  } catch (error) {
    console.log("获取应用信息失败:", error);
    return null;
  }
};

// 获取客户信息
const getCustomerInfo = async () => {
  try {
    const response = await httpService.get<any>(
      "/nxb/console/customer/listWithSummary",
      {
        pageNow: 1,
        pageSize: 10,
      }
    );

    if (response) {
      return response.customers[0].customerCode;
    }
    return null;
  } catch (error) {
    console.log("获取客户信息失败:", error);
    return null;
  }
};

// 登录后自动获取用户信息
const autoFetchUserInfo = async () => {
  const userInfoStr = localStorage.getItem("userInfo");

  if (userInfoStr) {
    // 获取应用信息
    const appInfo = await getApplicationInfo();

    if (appInfo) {
      appId.value = appInfo.appId;
      authorization.value = appInfo.appSecret;
      xencioPublicKey.value = appInfo.xencioPublicKey;
    }

    // 获取客户信息
    const customerCodeValue = await getCustomerInfo();
    if (customerCodeValue) {
      customerCode.value = customerCodeValue;
    }
  }
};

// 组件挂载时登录后自动获取用户信息
onMounted(() => {
  autoFetchUserInfo();
});

// 监听响应式变量变化，重新应用代码高亮
watch(
  [appId, authorization, customerCode, activeLanguage, response],
  () => {
    highlightCode();
  },
  { flush: "post" }
);

// 根据 appId、trxNumber、payAmount 自动计算 securityCode（MD5并大写）
watch(
  [appId, totalTrxNumber, totalRecord],
  () => {
    const base = `${appId.value}${totalTrxNumber.value}${totalRecord.value}`;
  },
  { immediate: true }
);

// 语言选项
const languages = [
  { id: "curl", name: "cURL" },
  { id: "javascript", name: "JavaScript" },
  { id: "python", name: "Python" },
  { id: "java", name: "Java" },
  { id: "csharp", name: "C#" },
  { id: "php", name: "PHP" },
  { id: "ruby", name: "Ruby" },
];

// 获取请求文本
const getRequestText = () => {
  // 根据当前语言生成支付制单的示例代码
  switch (activeLanguage.value) {
    case "curl":
      return (
        `curl -X POST '${
          import.meta.env.VITE_API_URL
        }/api/v2/payment/batchTransfer' \\\n` +
        `  -H 'x-app-id: ${appId.value}' \\\n` +
        `  -H 'x-app-secret: ${authorization.value}' \\\n` +
        `  -H 'x-customer-code: ${customerCode.value}' \\\n` +
        `  -H 'Content-Type: application/json' \\\n` +
        `  -d '{\"totalTrxNumber\":\"${totalTrxNumber.value}\",\"totalRecord\":\"${totalRecord.value}\",\"source\":\"${source.value}\",\"paymentType\":\"${paymentType.value}\",\"paymentDateTime\":\"${paymentDateTime.value}\",\"list\":${list.value}}'`
      );

    case "javascript":
      return (
        `const axios = require('axios');\\n\\n` +
        `const data = {\\n` +
        `  totalTrxNumber: '${totalTrxNumber.value}',\\n` +
        `  totalRecord: '${totalRecord.value}',\\n` +
        `  source: '${source.value}',\\n` +
        `  paymentType: '${paymentType.value}',\\n` +
        `  paymentDateTime: '${paymentDateTime.value}',\\n` +
        `  list: ${list.value}\\n` +
        `};\\n\\n` +
        `const config = {\\n` +
        `  method: 'post',\\n` +
        `  url: '${
          import.meta.env.VITE_API_URL
        }/api/v2/payment/batchTransfer',\\n` +
        `  headers: { \\n` +
        `  'x-app-id': '${appId.value}',\\n` +
        `  'x-app-secret': '${authorization.value}',\\n` +
        `  'x-customer-code': '${customerCode.value}',\\n` +
        `    'Content-Type': 'application/json'\\n` +
        `  },\\n` +
        `  data: JSON.stringify(data)\\n` +
        `};\\n\\n` +
        `axios(config)\\n` +
        `  .then(function (response) {\\n` +
        `    console.log(JSON.stringify(response.data));\\n` +
        `  })\\n` +
        `  .catch(function (error) {\\n` +
        `    console.log(error);\\n` +
        `  });`
      );

    case "python":
      return (
        `import requests\\n` +
        `import json\\n\\n` +
        `url = "${
          import.meta.env.VITE_API_URL
        }/api/v2/payment/batchTransfer"\\n\\n` +
        `payload = json.dumps({"totalTrxNumber":"${totalTrxNumber.value}","totalRecord":"${totalRecord.value}","source":"${source.value}","paymentType":"${paymentType.value}","paymentDateTime":"${paymentDateTime.value}","list":${list.value}})\\n` +
        `headers = {\\n` +
        `  'x-app-id': '${appId.value}',\\n` +
        `  'x-app-secret': '${authorization.value}',\\n` +
        `  'x-customer-code': '${customerCode.value}',\\n` +
        `  'Content-Type': 'application/json'\\n` +
        `}\\n\\n` +
        `response = requests.request("POST", url, headers=headers, data=payload)\\n\\n` +
        `print(response.text)`
      );

    case "java":
      return (
        `OkHttpClient client = new OkHttpClient().newBuilder()\\n` +
        `  .build();\\n` +
        `MediaType mediaType = MediaType.parse("application/json");\\n` +
        `RequestBody body = RequestBody.create(mediaType, "{\\\"totalTrxNumber\\\":\\\"${totalTrxNumber.value}\\\",\\\"totalRecord\\\":\\\"${totalRecord.value}\\\",\\\"source\\\":\\\"${source.value}\\\",\\\"paymentType\\\":\\\"${paymentType.value}\\\",\\\"paymentDateTime\\\":\\\"${paymentDateTime.value}\\\",\\\"list\\\":${list.value}}");\\n` +
        `Request request = new Request.Builder()\\n` +
        `  .url("${
          import.meta.env.VITE_API_URL
        }/api/v2/payment/batchTransfer")\\n` +
        `  .method("POST", body)\\n` +
        `  .addHeader("x-app-id", "${appId.value}")\\n` +
        `  .addHeader("x-app-secret", "${authorization.value}")\\n` +
        `  .addHeader("x-customer-code", "${customerCode.value}")\\n` +
        `  .addHeader("Content-Type", "application/json")\\n` +
        `  .build();\\n` +
        `Response response = client.newCall(request).execute();`
      );

    case "csharp":
      return (
        `var client = new RestClient("${
          import.meta.env.VITE_API_URL
        }/api/v2/payment/batchTransfer");\\n` +
        `var request = new RestRequest(Method.POST);\\n` +
        `request.AddHeader("x-app-id", "${appId.value}");\\n` +
        `request.AddHeader("x-app-secret", "${authorization.value}");\\n` +
        `request.AddHeader("x-customer-code", "${customerCode.value}");\\n` +
        `request.AddHeader("Content-Type", "application/json");\\n` +
        `request.AddParameter("application/json", "{\\\"totalTrxNumber\\\":\\\"${totalTrxNumber.value}\\\",\\\"totalRecord\\\":\\\"${totalRecord.value}\\\",\\\"source\\\":\\\"${source.value}\\\",\\\"paymentType\\\":\\\"${paymentType.value}\\\",\\\"paymentDateTime\\\":\\\"${paymentDateTime.value}\\\",\\\"list\\\":${list.value}}", ParameterType.RequestBody);\\n` +
        `IRestResponse response = client.Execute(request);\\n` +
        `Console.WriteLine(response.Content);`
      );

    case "php":
      return (
        `<?php\\n\\n` +
        `$curl = curl_init();\\n\\n` +
        `curl_setopt_array($curl, array(\\n` +
        `  CURLOPT_URL => '${
          import.meta.env.VITE_API_URL
        }/api/v2/payment/batchTransfer',\\n` +
        `  CURLOPT_RETURNTRANSFER => true,\\n` +
        `  CURLOPT_ENCODING => '',\\n` +
        `  CURLOPT_MAXREDIRS => 10,\\n` +
        `  CURLOPT_TIMEOUT => 0,\\n` +
        `  CURLOPT_FOLLOWLOCATION => true,\\n` +
        `  CURLOPT_HTTP_VERSION => CURL_HTTP_VERSION_1_1,\\n` +
        `  CURLOPT_CUSTOMREQUEST => 'POST',\\n` +
        `  CURLOPT_POSTFIELDS => '{\"totalTrxNumber\":\"${totalTrxNumber.value}\",\"totalRecord\":\"${totalRecord.value}\",\"source\":\"${source.value}\",\"paymentType\":\"${paymentType.value}\",\"paymentDateTime\":\"${paymentDateTime.value}\",\"list\":${list.value}}',\\n` +
        `  CURLOPT_HTTPHEADER => array(\\n` +
        `    'x-app-id: ${appId.value}',\\n` +
        `    'x-app-secret: ${authorization.value}',\\n` +
        `    'x-customer-code: ${customerCode.value}',\\n` +
        `    'Content-Type: application/json'\\n` +
        `  ),\\n` +
        `));\\n\\n` +
        `$response = curl_exec($curl);\\n\\n` +
        `curl_close($curl);\\n` +
        `echo $response;`
      );

    case "ruby":
      return (
        `require "uri"\\n` +
        `require "net/http"\\n\\n` +
        `url = URI("${
          import.meta.env.VITE_API_URL
        }/api/v2/payment/batchTransfer")\\n\\n` +
        `https = Net::HTTP.new(url.host, url.port);\\n` +
        `https.use_ssl = true\\n\\n` +
        `request = Net::HTTP::Post.new(url)\\n` +
        `request["x-app-id"] = "${appId.value}"\\n` +
        `request["x-app-secret"] = "${authorization.value}"\\n` +
        `request["x-customer-code"] = "${customerCode.value}"\\n` +
        `request["Content-Type"] = "application/json"\\n` +
        `request.body = "{\\\"totalTrxNumber\\\":\\\"${totalTrxNumber.value}\\\",\\\"totalRecord\\\":\\\"${totalRecord.value}\\\",\\\"source\\\":\\\"${source.value}\\\",\\\"paymentType\\\":\\\"${paymentType.value}\\\",\\\"paymentDateTime\\\":\\\"${paymentDateTime.value}\\\",\\\"list\\\":${list.value}}"\\n\\n` +
        `response = https.request(request)\\n` +
        `puts response.read_body`
      );

    default:
      return "";
  }
};

// 国密加密示例：按当前语言生成（批量接口路径与字段）
const getGmSampleText = () => {
  switch (activeLanguage.value) {
    case "curl":
      return (
        `# 新流程：\n` +
        `# 1) 生成 16 位字母数字 SM4 密钥 sm4Key\n` +
        `# 2) reqData = SM4_Encrypt(JSON(original), sm4Key)\n` +
        `# 3) sign = SM2_Sign(SM3(JSON(original)), enterprisePrivateKey)\n` +
        `# 4) xencio-key = SM2_Encrypt(sm4Key, xencioPublicKey)\n\n` +
        `curl --location '${
          import.meta.env.VITE_API_URL
        }/api/v2/payment/batchTransfer' \\n` +
        `  --header 'x-app-id: ${appId.value}' \\\n` +
        `  --header 'x-app-secret: ${authorization.value}' \\\n` +
        `  --header 'x-customer-code: ${customerCode.value}' \\\n` +
        `  --header 'Content-Type: application/json' \\\n` +
        `  --header 'xencio-key: <SM2_Encrypt(sm4Key)>' \\\n` +
        `  --data '{"reqData":"<SM4_Encrypt(original, sm4Key)>","sign":"<SM2_Sign(SM3(original), enterprisePrivateKey)>"}'`
      );

    case "javascript":
      return (
        `import { sm2 as sm2Lib, sm3 as sm3Lib, sm4 as sm4Lib } from 'sm-crypto';\n` +
        `import axios from 'axios';\n\n` +
        `const genSm4Key = () => Array.from({length:16},()=>('0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz'[Math.floor(Math.random()*62)])).join('');\n` +
        `const sm4Key = genSm4Key();\n\n` +
        `const originalObj = {\n` +
        `  totalTrxNumber: '${totalTrxNumber.value}',\n` +
        `  totalRecord: '${totalRecord.value}',\n` +
        `  source: '${source.value}',\n` +
        `  paymentType: '${paymentType.value}',\n` +
        `  paymentDateTime: '${paymentDateTime.value}',\n` +
        `  list: ${list.value}\n` +
        `};\n` +
        `const original = JSON.stringify(originalObj);\n` +
        `const reqData = sm4Lib.encrypt(original, sm4Key);\n` +
        `const signDigest = sm3Lib(original);\n\n` +
        `const xencioPub = '${xencioPublicKey.value}';\n` +
        `const sign = sm2Lib.doSignature(signDigest, '${sm2PrivateKey.value}', { hash: false });\n` +
        `const xencioKeyHeader = sm2Lib.doEncrypt(sm4Key, xencioPub, 1);\n\n` +
        `const headers = {\n` +
        `  'x-app-id': '${appId.value}',\n` +
        `  'x-app-secret': '${authorization.value}',\n` +
        `  'x-customer-code': '${customerCode.value}',\n` +
        `  'Content-Type': 'application/json',\n` +
        `  'xencio-key': xencioKeyHeader,\n` +
        `};\n` +
        `axios.post('${
          import.meta.env.VITE_API_URL
        }/api/v2/payment/batchTransfer', { reqData, sign }, { headers })\n` +
        `  .then(resp => {\n` +
        `    const encKey = resp.headers['xencio-key'] || '';\n` +
        `    const sm4KeyResp = sm2Lib.doDecrypt(encKey, '${sm2PrivateKey.value}', 1);\n` +
        `    const originResp = sm4Lib.decrypt(resp.data.respData || '', sm4KeyResp || '');\n` +
        `    const calc = sm3Lib(originResp || '');\n` +
        `    const verified = sm2Lib.doVerifySignature(calc, '${xencioPublicKey.value}', (resp.data.sign || ''), { hash: false });\n` +
        `    console.log({ verified, originResp });\n` +
        `  })\n` +
        `  .catch(console.error);`
      );

    case "python":
      return (
        `# pip install gmssl requests\n` +
        `import json, requests, secrets\n` +
        `from gmssl.sm4 import CryptSM4, SM4_ENCRYPT, SM4_DECRYPT\n` +
        `from gmssl.sm3 import sm3_hash\n` +
        `from gmssl.sm2 import CryptSM2\n\n` +
        `def gen_sm4_key():\n` +
        `    return ''.join(secrets.choice('0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz') for _ in range(16))\n\n` +
        `sm4_key = gen_sm4_key()\n` +
        `original_obj = {\n` +
        `  'totalTrxNumber': '${totalTrxNumber.value}',\n` +
        `  'totalRecord': '${totalRecord.value}',\n` +
        `  'source': '${source.value}',\n` +
        `  'paymentType': '${paymentType.value}',\n` +
        `  'paymentDateTime': '${paymentDateTime.value}',\n` +
        `  'list': ${list.value}\n` +
        `}\n` +
        `original = json.dumps(original_obj, ensure_ascii=False)\n\n` +
        `sm4 = CryptSM4()\n` +
        `sm4.set_key(sm4_key.encode('utf-8'), SM4_ENCRYPT)\n` +
        `reqData = sm4.crypt_ecb(original.encode('utf-8')).hex()\n\n` +
        `sign_hex = sm3_hash(list(original.encode('utf-8')))\n` +
        `sm2_signer = CryptSM2(public_key='', private_key='${sm2PrivateKey.value}')\n` +
        `sign = sm2_signer.sign(bytes.fromhex(sign_hex))\n\n` +
        `xencio_pub = '${xencioPublicKey.value}'\n` +
        `sm2_enc = CryptSM2(public_key=xencio_pub, private_key='')\n` +
        `xencio_key_header = sm2_enc.encrypt(sm4_key.encode('utf-8')).hex()\n\n` +
        `url = '${
          import.meta.env.VITE_API_URL
        }/api/v2/payment/batchTransfer'\n` +
        `headers = {\n` +
        `  'x-app-id': '${appId.value}',\n` +
        `  'x-app-secret': '${authorization.value}',\n` +
        `  'x-customer-code': '${customerCode.value}',\n` +
        `  'Content-Type': 'application/json',\n` +
        `  'xencio-key': xencio_key_header,\n` +
        `}\n` +
        `payload = json.dumps({ 'reqData': reqData, 'sign': sign }, ensure_ascii=False)\n` +
        `resp = requests.post(url, headers=headers, data=payload)\n` +
        `enc_key_hex = resp.headers.get('xencio-key', '')\n` +
        `sm2_priv = CryptSM2(public_key='', private_key='${sm2PrivateKey.value}')\n` +
        `sm4_key_resp = sm2_priv.decrypt(bytes.fromhex(enc_key_hex)).decode('utf-8')\n` +
        `data = resp.json()\n` +
        `sm4_dec = CryptSM4()\n` +
        `sm4_dec.set_key(sm4_key_resp.encode('utf-8'), SM4_DECRYPT)\n` +
        `origin_resp = sm4_dec.crypt_ecb(bytes.fromhex(data.get('respData',''))).decode('utf-8')\n` +
        `calc_hex = sm3_hash(list(origin_resp.encode('utf-8')))\n` +
        `sm2_pub = CryptSM2(public_key='${xencioPublicKey.value}', private_key='')\n` +
        `verified = sm2_pub.verify(sign, bytes.fromhex(calc_hex))\n` +
        `print({ 'verified': verified, 'originResp': origin_resp })\n`
      );

    case "java":
      return `// 参考单笔页面 Java 示例，将 original 替换为批量 JSON 并请求 batchTransfer。\n`;

    case "csharp":
      return `// 参考单笔页面 C# 示例，将 original 替换为批量 JSON 并请求 batchTransfer。\n`;

    case "php":
      return `// 建议使用: packagist 上的 lizhichao/sm-crypto 或 yansongda/gm。流程同：SM4(ECB+PKCS7) + SM3 + SM2。\n`;

    case "ruby":
      return `# Ruby 可搜索 gm/国密相关库或调用 C 扩展，流程与 JS 保持一致。\n`;

    default:
      return "";
  }
};

// 支付制单提交
const submitPayment = async () => {
  if (!appId.value) {
    alert("请填写应用ID (x-app-id)");
    return;
  }
  if (
    !totalTrxNumber.value ||
    !totalRecord.value ||
    !source.value ||
    !paymentType.value ||
    !list.value
  ) {
    alert(
      "请填写必填字段：totalTrxNumber、totalRecord、source、paymentType、list"
    );
    return;
  }
  if (paymentType.value === "appoint" && !paymentDateTime.value) {
    alert("当 paymentType=appoint 时，paymentDateTime 不可为空");
    return;
  }

  loading.value = true;
  response.value = "";
  try {
    let bodyToSend: any = null;
    const headers = {
      "x-app-id": appId.value,
      "x-app-secret": authorization.value,
      "x-customer-code": customerCode.value,
      "Content-Type": "application/json",
    } as Record<string, string>;
    if (acceptHeader.value) headers["Accept"] = acceptHeader.value;

    if (!enableGm.value) {
      bodyToSend = {
        totalTrxNumber: totalTrxNumber.value,
        totalRecord: totalRecord.value,
        source: source.value,
        paymentType: paymentType.value,
        paymentDateTime: paymentDateTime.value,
        list: list.value,
      };
    } else {
      // 国密加密请求构建
      if (!xencioPublicKey.value || !sm2PrivateKey.value) {
        response.value = JSON.stringify(
          { error: "请先填写见知公钥与企业SM2私钥后再启用国密加密。" },
          null,
          2
        );
        loading.value = false;
        return;
      }
      await ensureSmCrypto();
      const originalPayload = {
        totalTrxNumber: totalTrxNumber.value || "",
        totalRecord: totalRecord.value || "",
        source: source.value || "",
        paymentType: paymentType.value || "",
        paymentDateTime: paymentDateTime.value || "",
        // 确保加密用的原始报文 list 为数组
        list: (() => {
          try {
            return JSON.parse(list.value || "[]");
          } catch {
            return [];
          }
        })(),
      };
      const rawJson = JSON.stringify(originalPayload);
      const sm4Key = randomSm4Key();
      const sm4KeyHex = Array.from(new TextEncoder().encode(sm4Key))
        .map((b) => b.toString(16).padStart(2, "0"))
        .join("");
      const reqData = sm4Lib.encrypt(rawJson, sm4KeyHex);
      const signDigest = sm3Lib(rawJson);
      // 私钥、公钥从 Base64 转换为 HEX，用于 sm-crypto
      const sm2PrivHex = normalizeSm2PrivateHex(sm2PrivateKey.value);
      const xencioPubHex = normalizeSm2PublicHex(xencioPublicKey.value);
      const sign = sm2Lib.doSignature(signDigest, sm2PrivHex, { hash: false });
      const xencioKeyHeader = sm2Lib.doEncrypt(sm4Key, xencioPubHex, 1);
      headers["xencio-key"] = xencioKeyHeader;
      bodyToSend = { reqData, sign };
    }

    const res = await fetch(serverUrl + "/api/v2/payment/batchTransfer", {
      method: "POST",
      headers,
      body: JSON.stringify(bodyToSend),
    });

    const text = await res.text();
    if (enableGm.value) {
      // 尝试解析国密响应
      try {
        await ensureSmCrypto();
        const encKey = res.headers.get("xencio-key") || "";
        const sm4KeyResp = sm2Lib.doDecrypt(encKey, sm2PrivateKey.value, 1);
        const sm4KeyRespHex = Array.from(new TextEncoder().encode(sm4KeyResp))
          .map((b) => b.toString(16).padStart(2, "0"))
          .join("");
        const json = JSON.parse(text || "{}");
        const originResp = sm4Lib.decrypt(
          json.respData || "",
          sm4KeyRespHex || ""
        );
        const calc = sm3Lib(originResp || "");
        const verified = sm2Lib.doVerifySignature(
          calc,
          xencioPublicKey.value,
          json.sign || "",
          { hash: false }
        );
        response.value = JSON.stringify({ verified, originResp }, null, 2);
      } catch (e) {
        response.value = text;
      }
    } else {
      response.value = text;
    }
  } catch (error) {
    response.value = JSON.stringify(
      { error: "请求失败: " + (error as Error).message },
      null,
      2
    );
  } finally {
    loading.value = false;
  }
};
</script>

<style scoped>
/* 添加任何需要的自定义样式 */
</style>
